import React, { FC, useContext } from 'react';
import { GroupNameContainerStyled } from './styled';
import { PersonaType, CommodityAssignee } from '@bechtel/types';
import { map, filter, find } from 'lodash';
import { useName, WeldMilestoneType } from '@bechtel/client-common';
import {
  Text,
  AvatarGroup,
  AvatarSize,
  AvatarGroupSpacing,
  Box,
  AvatarComponentProps,
  useMediaQuery,
  belowBreakpoint,
  Theme,
} from '@bechtel/gds';
import { ThemeContext } from 'styled-components';
import { Trans } from '@lingui/macro';
import { WeldAvatarGroupProps } from './types';

export const WeldAvatarGroup: FC<WeldAvatarGroupProps> = ({ weldAssignees, personaType, currentMilestoneType }) => {
  const { getFullName } = useName();
  const welders = filter(weldAssignees, (weldAssignee) => weldAssignee.personaType === PersonaType.CRAFT);
  const isFitupMilestone = currentMilestoneType?.name?.toUpperCase() == WeldMilestoneType.FITUP;
  const { breakpoints } = useContext<Theme>(ThemeContext);
  const isDesktop = useMediaQuery(belowBreakpoint(breakpoints.desktop));

  const welderAvatars: AvatarComponentProps[] = welders
    ? map(welders, (welderItem: CommodityAssignee) => {
        const welderName = getFullName(
          welderItem?.assignee?.firstName ? welderItem?.assignee?.firstName : ' ',
          welderItem?.assignee?.lastName ? welderItem?.assignee?.lastName : ' '
        );
        return {
          src: welderItem?.assignee?.imageUrl ? welderItem?.assignee?.imageUrl : '',
          alt: welderItem?.assignee?.badgeNumber ? `(${welderItem?.assignee?.badgeNumber}) ${welderName}` : welderName,
        };
      })
    : [];

  const weldFieldEngineer = find(weldAssignees, (weldAssignee) => {
    return weldAssignee.personaType === PersonaType.FIELD_WELD_ENGINEER;
  });

  const weldFieldEngineerAvatar: AvatarComponentProps | null = weldFieldEngineer
    ? {
        src: weldFieldEngineer?.assignee?.imageUrl ? weldFieldEngineer?.assignee?.imageUrl : '',
        alt: getFullName(
          weldFieldEngineer?.assignee?.firstName ? weldFieldEngineer?.assignee?.firstName : '',
          weldFieldEngineer?.assignee?.lastName ? weldFieldEngineer?.assignee?.lastName : ''
        ),
      }
    : null;

  const weldForeman = find(weldAssignees, (weldAssignee) => {
    return weldAssignee.personaType === PersonaType.FOREMAN;
  })?.assignee;

  const weldForemanAvatar: AvatarComponentProps | null = {
    src: weldForeman?.imageUrl ? weldForeman?.imageUrl : ' ',
    alt: getFullName(
      weldForeman?.firstName ? weldForeman?.firstName : '',
      weldForeman?.lastName ? weldForeman?.lastName : ''
    ),
  };

  return (
    <Box>
      {welders.length ? (
        <Box flexDirection={{ sm: 'column' }} alignItems={{ sm: 'center' }} css={{ position: 'relative' }}>
          <AvatarGroup
            data-testid="stencils-avatar-group"
            maxCount={isDesktop ? 1 : 3}
            data={welderAvatars}
            size={AvatarSize.LARGE}
            spacing={AvatarGroupSpacing.SMALL}
          />
          <GroupNameContainerStyled>
            <Box alignItems={{ sm: 'center' }} justifyContent={{ sm: 'center' }}>
              <Text size="200" family="graphik" color="grey.700">
                <Trans>Welder(s)</Trans>
              </Text>
            </Box>
          </GroupNameContainerStyled>
        </Box>
      ) : null}
      {weldFieldEngineer && (
        <Box
          alignItems={{ sm: 'center' }}
          flexDirection={{ sm: 'column' }}
          ml={{ sm: '450' }}
          css={{ position: 'relative' }}
        >
          <AvatarGroup
            data-testid="fieldEngineer-avatar-group"
            maxCount={1}
            data={[weldFieldEngineerAvatar]}
            size={AvatarSize.LARGE}
            spacing={AvatarGroupSpacing.SMALL}
          />
          <GroupNameContainerStyled>
            <Box alignItems={{ sm: 'center' }} justifyContent={{ sm: 'center' }}>
              <Text size="200" family="graphik" color="grey.700">
                <Trans>FWE</Trans>
              </Text>
            </Box>
          </GroupNameContainerStyled>
        </Box>
      )}
      {weldForeman && personaType != PersonaType.FOREMAN && (
        <Box
          alignItems={{ sm: 'center' }}
          flexDirection={{ sm: 'column' }}
          ml={{ sm: '450' }}
          css={{ position: 'relative' }}
        >
          <AvatarGroup
            data-testid="foreman-avatar-group"
            maxCount={1}
            data={[weldForemanAvatar]}
            size={AvatarSize.LARGE}
            spacing={AvatarGroupSpacing.SMALL}
          />
          <GroupNameContainerStyled>
            <Box alignItems={{ sm: 'center' }} justifyContent={{ sm: 'center' }}>
              <Text size="200" family="graphik" color="grey.700">
                <Trans>FM</Trans>
              </Text>
            </Box>
          </GroupNameContainerStyled>
        </Box>
      )}
    </Box>
  );
};
